import { Alert } from "@mui/lab";
import { AppBar, Card, CardContent, FormControl, FormControlLabel, FormGroup, FormHelperText, Input, InputLabel, ListItemText, MenuItem, Select, Switch, Tab, TextField, Toolbar } from "@mui/material";
import Grid from "@mui/material/Grid";
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import { SquarePicture } from "assets/appConfig";
import { AppImages } from "assets/appResources";
import { FormStyleClasses, GlobalClassKeys, GrayBarStyle, TabLargeStyle } from "assets/jss/common-styles";
import { AppBarTabs } from "components/AppTabs";
import { AwardSnackbarMessage } from "components/AwardSnackbarMessage";
import ButtonBack from "components/Buttons/ButtonBack";
import ButtonSave from "components/Buttons/ButtonSave";
import { RequestIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import { ImageSelection } from "components/FileUpload/ImageSelection";
import CardHeaderColor from "components/MuiExtras/Card/CardHeaderColor";
import MuiCompareTextField from "components/MuiExtras/CustomInput/MuiCompareTextField";
import MuiPasswordTextField from "components/MuiExtras/CustomInput/MuiPasswordTextField";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import MuiKeyboardDatePicker from "components/MuiExtras/MuiKeyboardDatePicker";
import MuiMessageBlock from "components/MuiExtras/MuiMessageBlock";
import MuiPaper from "components/MuiExtras/MuiPaper";
import { ReactSplitString } from "components/ReactSplitString";
import { WithSnackbar, withSnackbar } from 'components/SnackbarProvider/Snackbar';
import LibUtils, { validateEmail } from "lib/LibUtils";
import MediaUtils from "lib/MediaUtils";
import { AccountType, SystemRoles, UserKnowsAboutSpecialistGrantMethod } from "models/ApiEnums";
import { GetEditUserDto, GroupShortView, SaveUserDto, UserKnowsAboutPostDto, UserPhoneModel, UserRecordFormConfigDto } from "models/ApiModels";
import { RequestLifeCycle } from "models/AppModels";
import moment from "moment";
import * as React from "react";
import { useTranslation, WithTranslation, withTranslation } from "react-i18next";
import { Prompt } from "react-router";
import Session from "services/Session";
import ManagerAutoComplete from "views/User/Components/ManagerAutoComplete";
import UserEditCredentials from "views/User/Components/UserEditCredentials";
import UserEditGroup from "views/User/Components/UserEditGroup";
import UserEditKnowsAbout from "views/User/Components/UserEditKnowsAbout";
import UserEditPhones from "views/User/Components/UserEditPhones";
import UserEditRole from "views/User/Components/UserEditRole";
import UserEditSocialNetworks from "views/User/Components/UserEditSocialNetworks";
import UserLogDialog from "views/User/Dialogs/UserLogDialog";
import { WidgetUserAbout } from "../Widgets/WidgetUserAbout";
import LocationAutoComplete from "./LocationAutoComplete";

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    gobackMessage?: string;
    userId?: string;
    onClose: (reload: boolean) => void;
}

interface IState {
    dialog: any,
    currentTab: TabOptions,
    currentLockout: boolean,
    formCycle: RequestLifeCycle,
    lastError: any,
    record: Partial<GetEditUserDto>,
    validate: {
        culture: boolean,
        email: boolean,
        emailUnique: boolean,
        location: boolean,
        name: boolean,
        nameUnique: boolean,
        phones: boolean,
        passwordRule: boolean,
        passwordCompare: boolean;
        thumbnail: boolean,
        timezoneId: boolean,
    };
}

type Props = IProps & WithStyles<typeof FormStyleClasses> & WithSnackbar & WithTranslation;

enum TabOptions { Manager, Personal }

/////////////////////////////////////////////////////////////////////////////////////

export const RenderAccountTopHeaderInfo: React.FC<{ accountType?: AccountType; }> = ({ accountType }) => {
    const { t } = useTranslation();

    if (accountType != null && (accountType & AccountType.Saml) > 0) {
        return <Alert severity="warning">{t("Views.User.Common.AccountType.Saml")}</Alert>;
    }

    if (accountType != null && (accountType & AccountType.WebIntegation) > 0) {
        return <Alert severity="warning">{t("Views.User.Common.AccountType.WebIntegation")}</Alert>;
    }

    return null;
};

/////////////////////////////////////////////////////////////////////////////////////

class UserEdit extends React.Component<Props, IState> {

    private config: UserRecordFormConfigDto;
    private loadDto: GetEditUserDto = null;

    ///
    constructor(props: Props) {
        super(props);

        this.loadDto = null;

        this.state = {
            formCycle: RequestLifeCycle.Init,
            currentTab: TabOptions.Manager,
            currentLockout: null,
            dialog: null,
            lastError: null,
            record: null,
            validate: {
                culture: Session.uiCulture != null,
                email: null,
                emailUnique: null,
                location: null,
                name: null,
                nameUnique: null,
                passwordCompare: true,
                passwordRule: true,
                phones: true,
                thumbnail: true,
                timezoneId: Session.timezone != null,
            },
        };
    }

    ///
    private res(r: string, options?: any) {
        return this.props.t("Views.User.UserEdit." + r, options);
    }

    ///
    private getEmptyDto(): GetEditUserDto {
        var blockCodeId = this.config.hasLicenceAvailable
            ? null
            : this.config.blockCodes.find(p => p.value.externalCode == "BLOCKED")?.key;

        return {
            userId: null,
            name: "",
            email: "",
            timezoneId: Session.timezone,
            culture: Session.uiCulture,
            thumbnail: null,
            locationId: null,
            locationName: null,
            blockCodeId: blockCodeId,
            groups: [],
            ownRoles: SystemRoles.None,
            groupsRoles: SystemRoles.None,
            knowsAbout: [],
            knowledgeTopics: [],
            corp: { manager: null, admissionDate: null, resignationDate: null },
            account: { accountExpirationDate: null, isLockedOut: false, accountConfirmationDate: null, lastPasswordChangeDate: null, passwordExpirationDate: null, accountType: AccountType.Local },
            personal: { phones: [], socialNetworks: [], credentials: [] },

            newPassword: null,
            confirmPassword: null,
            generateAndSentPassword: true,

            recordInfo: null,
            counters: null,
            deleted: false,
            blocked: blockCodeId != null,
            canChangeBlockCode: this.config.hasLicenceAvailable
        };
    }

    ///
    private init() {
        api.getUserFormEditOptions(Session.user.culture)
            .then((config) => {
                this.config = config;

                if (this.props.userId != null && this.props.userId != "create")
                    this.continueLoadUser(this.props.userId);
                else {
                    const newUser = this.getEmptyDto();
                    this.loadDto = LibUtils.deepCopy(newUser);
                    this.setState({ formCycle: RequestLifeCycle.InitSuccess, record: newUser });
                }
            })
            .catch((error: any) => {
                this.setState({
                    lastError: error,
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private continueLoadUser(userId: string) {
        api.getUserRecord(userId)
            .then((dto) => {

                var record: GetEditUserDto = {
                    ...dto,
                    generateAndSentPassword: false,
                    newPassword: null,
                    confirmPassword: null
                };

                this.loadDto = LibUtils.deepCopy(record);

                this.setState({
                    formCycle: RequestLifeCycle.InitSuccess,
                    currentLockout: dto.account.isLockedOut,
                    record: record,
                    validate: {
                        culture: true,
                        name: true,
                        nameUnique: true,
                        email: true,
                        emailUnique: true,
                        location: (this.config.chooseLocalFromList && record.locationId != null) || (!this.config.chooseLocalFromList && record.locationName != null),
                        passwordCompare: true,
                        passwordRule: true,
                        phones: true,
                        thumbnail: true,
                        timezoneId: true,
                    }
                });
            })
            .catch((error: any) => {
                this.setState({
                    lastError: error,
                    formCycle: RequestLifeCycle.InitFailed
                });
            });
    }

    ///
    private isValidForm(): boolean {
        return LibUtils.isAllPropertiesWithValue(this.state.validate, true);
    }

    ///
    private getPostDto(): SaveUserDto {
        const { record } = this.state;

        return {
            userId: record.userId,
            name: record.name,
            email: record.email,
            timezoneId: record.timezoneId,
            culture: record.culture,

            thumbnail: record.thumbnail == null ? null : { fileId: record.thumbnail.fileId },

            blockCodeId: record.blockCodeId,
            groups: record.groups.map((v) => v.id),
            locationId: record.locationId,
            locationName: record.locationName,
            ownRoles: record.ownRoles,

            generateAndSentPassword: record.generateAndSentPassword,
            newPassword: record.newPassword,

            knowsAbout: record.knowsAbout.map((v) => {
                return {
                    topicId: v.topicId,
                    managerEndorsementOption: v.currentEndorser != null,
                    managerSpecialistOption: v.specialist.specialistGrantMethod == UserKnowsAboutSpecialistGrantMethod.UserManager
                } as UserKnowsAboutPostDto;
            }),


            account: {
                accountExpirationDate: record.account.accountExpirationDate,
                isLockedOut: record.account.isLockedOut
            },

            corp: {
                managerId: record.corp.manager ? record.corp.manager.id : null,
                admissionDate: record.corp.admissionDate,
                resignationDate: record.corp.resignationDate
            },

            personal: {
                credentials: record.personal.credentials,
                phones: record.personal.phones,
                socialNetworks: record.personal.socialNetworks,
            },
        };
    }

    ///
    private handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        this.updateRecord(
            event.target.name,
            event.target.type == "checkbox"
                ? (event.target as HTMLInputElement).checked
                : (event.target as HTMLInputElement).valueAsNumber
                    ? (event.target as HTMLInputElement).valueAsNumber
                    : event.target.value
        );
    };

    ///
    private updateRecord(name: string, v: any) {
        this.setState(state => {
            switch (name) {
                case "name":
                    state.record.name = v;
                    state.validate.name = v != "";
                    break;
                case "email":
                    state.record.email = v;
                    state.validate.email = v != "" && validateEmail(v);
                    break;
                case "ddlTimezoneId":
                    state.record.timezoneId = v;
                    state.validate.timezoneId = v != "";
                    break;
                case "ddlCulture":
                    state.record.culture = v;
                    state.validate.culture = v != "";
                    break;
                case "generatePassword":
                    state.record.generateAndSentPassword = v;
                    if (v === true)
                        state.validate.passwordCompare = state.validate.passwordRule = true;
                    else {
                        state.validate.passwordCompare = state.validate.passwordRule = false;
                        state.record.newPassword = state.record.confirmPassword = "";
                    }
                    break;

                case "thumbnail":
                    if (v == null) state.record.thumbnail = null;
                    else state.record.thumbnail = { fileId: v.fileId, url: v.tempUrl };
                    break;

                case "thumbnailUploading":
                    state.validate.thumbnail = !v;
                    break;

                case "location":
                    state.record.locationId = v == null ? null : v.key;
                    state.record.locationName = v == null ? null : v.label;
                    state.validate.location = v != null && v.key != "";
                    break;

                case "locationName":
                    state.record.locationName = v;
                    state.validate.location = v != "";
                    break;

                case "blockCode":
                    state.record.blockCodeId = v;
                    break;

                case "accountExpirationDate":
                    state.record.account.accountExpirationDate = v;
                    break;

                case "isLockedOut":
                    state.record.account.isLockedOut = v;
                    break;

                case "manager":
                    state.record.corp.manager = v;
                    break;
                case "resignationDate":
                    state.record.corp.resignationDate = v;
                    break;
                case "admissionDate":
                    state.record.corp.admissionDate = v;
                    break;

                case "groups":
                    var newRoles = SystemRoles.None;
                    (v as GroupShortView[]).forEach((g) => newRoles |= g.roles);

                    state.record.groups = v;
                    state.record.groupsRoles = newRoles;
                    break;

                case "ownRoles":
                    state.record.ownRoles = v;
                    break;

                case "phones":
                    state.record.personal.phones = v;
                    state.validate.phones = (v as UserPhoneModel[]).findIndex(p => p.isValid === false) == -1;
                    break;

                case "networks":
                    state.record.personal.socialNetworks = v;
                    break;

                case "credentials":
                    state.record.personal.credentials = v;
                    break;

                case "knowsAbout":
                    state.record.knowsAbout = v;
                    break;
            }
            return state;
        });
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        if (this.isValidForm()) {

            const dto = this.getPostDto();

            this.setState({
                formCycle: RequestLifeCycle.Posting,
                lastError: null,
            });

            api.saveUserRecord(dto)
                .then((result) => {
                    if (Session.user.id == result.account.userId) {
                        Session.updateUserInfo(result.account);
                    }

                    if (result.insertedWithoutLicense) {
                        this.props.snackbar.showMessage(this.res("InsertedWithoutLicenseInfo"), "warning");
                    }
                    else if (result.award) {
                        this.props.snackbar.showMessage(<AwardSnackbarMessage award={result.award} />, "trophy");
                    }
                    else {
                        this.props.snackbar.showMessage(this.res("UpdateSuccess"), "success");
                    }

                    this.props.onClose(true);
                })
                .catch((error: any) => {
                    this.setState({
                        lastError: error,
                        formCycle: RequestLifeCycle.PostFailed
                    });
                });
        }
    }

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        this.setState({ currentTab: value });
    };

    ///
    private isRecordChanged() {
        return this.state.lastError == null && !LibUtils.deepCompare(this.state.record, this.loadDto);
    }

    ///
    private checkEmail = () => {
        api.checkUserEmailIsUnique(this.state.record.userId, this.state.record.email)
            .then((result) => {
                this.setState(state => {
                    state.validate.emailUnique = result;
                    return state;
                });
            });
    };

    ///
    private checkUsername = () => {
        api.checkUserNameIsUnique(this.state.record.userId, this.state.record.name)
            .then((result) => {
                this.setState(state => {
                    state.validate.nameUnique = result;
                    return state;
                });
            });
    };

    ///
    private renderToolbar() {
        var unsaveMessage = this.props.t("Common.Question.Unsave");

        return (
            <AppBar position="sticky" color="default" className={GlobalClassKeys.stickyToolbar}>
                <Toolbar>

                    <div className={GlobalClassKeys.grow}>
                        <ButtonBack
                            text={this.props.gobackMessage}
                            onClick={() => {
                                if (this.isRecordChanged()) {
                                    if (window.confirm(unsaveMessage)) {
                                        this.props.onClose(false);
                                    }
                                }
                                else {
                                    this.props.onClose(false);
                                }
                            }}
                        />
                    </div>

                    {
                        !this.state.record.deleted &&
                        <ButtonSave
                            disabled={!this.isValidForm()}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                        />
                    }
                </Toolbar>
            </AppBar>
        );
    }

    ///
    private renderRecordInfo() {
        return this.state.record.recordInfo
            ? (
                <MuiContainer margin="normal">
                    <WidgetUserAbout
                        record={this.state.record.recordInfo}
                        counters={this.state.record.counters}
                        lastAccess={this.state.record.account.lastLogin}
                        onShowHistory={
                            () => {
                                this.setState({
                                    dialog:
                                        <UserLogDialog
                                            userId={this.state.record.userId}
                                            onClose={() => this.setState({ dialog: null })}
                                        />
                                });
                            }
                        }
                    />
                </MuiContainer>
            )
            : null;
    }

    ///
    private renderPassword() {
        if (this.state.record.userId != null)
            return null;

        const { record } = this.state;

        return (
            <>
                <FormGroup>
                    <FormControlLabel
                        control={
                            <Switch
                                name="generatePassword"
                                checked={record.generateAndSentPassword}
                                onChange={this.handleChange}
                                value="isLockedOut"
                                color="secondary"
                            />
                        }
                        label={this.res("GeneratePasswordLabel")}
                    />
                </FormGroup>

                {
                    !record.generateAndSentPassword &&
                    <>
                        <MuiPasswordTextField
                            label={this.res("PasswordLabel")}
                            autoFocus={true}
                            fullWidth={true}
                            maxLength={16}
                            minLength={this.config.authOptions.policies.passwordMinLength}
                            minNumericChars={this.config.authOptions.policies.passwordMinNumericChars}
                            minSpecialChars={this.config.authOptions.policies.passwordMinSpecialChars}
                            minLowerChars={this.config.authOptions.policies.passwordMinLowerChars}
                            minUpperChars={this.config.authOptions.policies.passwordMinUpperChars}
                            margin="dense"
                            autoComplete="off"
                            value={record.newPassword}
                            onChange={
                                (text, isValid) => {
                                    this.setState(
                                        (current) => (
                                            {
                                                record: { ...current.record, ...{ newPassword: text } },
                                                validate: { ...current.validate, ...{ passwordRule: isValid, passwordCompare: text == current.record.confirmPassword } }
                                            }
                                        )
                                    );
                                }
                            }
                        />

                        <MuiCompareTextField
                            label={this.res("ConfirmPasswordLabel")}
                            fullWidth={true}
                            maxLength={16}
                            margin="dense"
                            value={record.confirmPassword}
                            valueToCompare={this.state.record.newPassword}
                            onChange={
                                (text) => {
                                    this.setState(
                                        (current) => (
                                            {
                                                record: { ...current.record, ...{ confirmPassword: text } },
                                                validate: { ...current.validate, ...{ passwordCompare: text == current.record.newPassword } }
                                            }
                                        )
                                    );
                                }
                            }

                        />
                    </>
                }
            </>
        );
    }

    ///
    private renderFormMain() {

        const { record, validate } = this.state;

        return (
            <MuiContainer margin="normal" backdrop={record.deleted}>

                <form noValidate autoComplete="off" >

                    <MuiPaper>
                        <Grid container spacing={2}>
                            <Grid item style={{ width: 134 }}>
                                <ImageSelection
                                    acceptedFiles={MediaUtils.AcceptImages()}
                                    allowEditSelectedImage={true}
                                    currentImageUrl={record && record.thumbnail ? record.thumbnail.url : null}
                                    dimensions={SquarePicture}
                                    initialSource="local"
                                    emptyImageUrl={AppImages.DefaultUserThumbnail}
                                    enableClear={true}
                                    onClear={() => { this.updateRecord("thumbnail", null); }}
                                    onFileUploadStarted={() => this.updateRecord("thumbnailUploading", true)}
                                    onFileUploadFinished={() => this.updateRecord("thumbnailUploading", false)}
                                    onFileUploadSuccess={(data) => this.updateRecord("thumbnail", data.thumbnail ? data.thumbnail : data.mainFile)}
                                />
                            </Grid>

                            <Grid item xs >

                                <FormGroup>
                                    {
                                        record.deleted &&
                                        <MuiMessageBlock center variant="error" message={this.res("AccountDeletedMessage")} />
                                    }
                                </FormGroup>

                                <FormGroup>
                                    <TextField
                                        autoFocus
                                        name="name"
                                        label={this.props.t<string>("Common.Literals.NameLabel")}
                                        autoComplete="off"
                                        error={validate.name === false || validate.nameUnique === false}
                                        margin="dense"
                                        value={record.name || ""}
                                        onChange={this.handleChange}
                                        onBlur={this.checkUsername}
                                        fullWidth
                                        required
                                        inputProps={{
                                            maxLength: 35,
                                        }}
                                    />
                                    {validate.nameUnique === false && <FormHelperText error>{this.props.t("Views.Public.Register.NameAlreadyExists")}</FormHelperText>}
                                </FormGroup>

                                <FormGroup>
                                    <TextField
                                        name="email"
                                        label={this.props.t<string>("Common.Literals.EmailLabel")}
                                        margin="dense"
                                        error={validate.email === false || validate.emailUnique === false}
                                        value={record.email || ""}
                                        onChange={this.handleChange}
                                        onBlur={this.checkEmail}
                                        disabled={record.account?.accountType != AccountType.Local}
                                        required
                                        fullWidth
                                        inputProps={{
                                            maxLength: 255,
                                        }}
                                    />
                                    {validate.emailUnique === false && <FormHelperText error>{this.props.t("Views.Public.Register.EmailAlreadyExists")}</FormHelperText>}
                                </FormGroup>

                                <FormGroup>
                                    {
                                        this.config.chooseLocalFromList
                                            ? (
                                                <LocationAutoComplete
                                                    label={this.config.localName}
                                                    emptySelectionLabel={this.res("LocationEmptySelectionLabel")}
                                                    required={true}
                                                    error={validate.location === false}
                                                    margin="dense"
                                                    selectedItems={record.locationId == null ? null : [{ key: record.locationId, label: record.locationName, canDelete: true }]}
                                                    onChange={(data) => { this.updateRecord("location", data.length == 0 ? null : data[0]); }}
                                                />
                                            )
                                            : (
                                                <TextField
                                                    name="locationName"
                                                    label={this.config.localName}
                                                    error={validate.location === false}
                                                    value={record.locationName || ""}
                                                    margin="dense"
                                                    placeholder={this.res("LocationNamePlaceholder")}
                                                    onChange={this.handleChange}
                                                    autoComplete="nope"
                                                    autoCorrect="off"
                                                    required
                                                    fullWidth
                                                    inputProps={{
                                                        maxLength: 255,
                                                    }}
                                                />
                                            )
                                    }
                                </FormGroup>

                                <Grid container spacing={1}>

                                    <Grid item xs={12} md={6}>

                                        <FormControl fullWidth margin="dense">
                                            <InputLabel shrink id="LabelBlockCodeId">{this.res("BlockLabel")}</InputLabel>
                                            <Select
                                                labelId="LabelBlockCodeId"
                                                autoWidth
                                                displayEmpty
                                                name="blockCode"
                                                value={record.blockCodeId || ""}
                                                disabled={!record.canChangeBlockCode}
                                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleChange(e)}
                                                input={<Input id="LabelBlockCodeId" />}
                                            >
                                                {
                                                    (this.config.hasLicenceAvailable || (!this.config.hasLicenceAvailable && record.userId != null && !record.blocked)) &&
                                                    <MenuItem value="">{this.res("UnblockedMenuText")}</MenuItem>
                                                }
                                                {
                                                    this.config.blockCodes.filter(x => !record.canChangeBlockCode || x.value.externalCode != "SSOBLOCKED").map((v) => <MenuItem key={v.key} value={v.key}>{v.value.description}</MenuItem>)
                                                }
                                            </Select>
                                        </FormControl>

                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <MuiKeyboardDatePicker
                                            fullWidth
                                            clearable
                                            margin="dense"
                                            label={this.res("AccountExpiresUntilLabel")}
                                            emptyLabel={this.res("UnlimedAccessText")}
                                            value={record.account == null ? null : record.account.accountExpirationDate}
                                            onChange={(date) => { this.updateRecord("accountExpirationDate", date?.toDate()); }}
                                        />
                                    </Grid>
                                </Grid>

                                {
                                    false &&
                                    <FormControl fullWidth margin="dense">
                                        <InputLabel htmlFor="LabelTimezone">{this.props.t("Common.Literals.TimezoneLabel")}</InputLabel>
                                        <Select
                                            name="ddlTimezoneId"
                                            error={validate.timezoneId === false}
                                            value={record.timezoneId}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleChange(e)}
                                            inputProps={{ name: 'ddlTimezoneId', id: 'LabelTimezone' }}
                                            autoWidth
                                        >
                                            {this.config.timezones.map((v) => (
                                                <MenuItem key={v.zoneId} value={v.zoneId}>
                                                    <ListItemText primary={`${v.zoneId} (${v.name})`} disableTypography />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                }

                                {
                                    false &&
                                    <FormControl fullWidth margin="dense" >
                                        <InputLabel htmlFor="ddlCulture">{this.props.t("Common.Literals.CultureLabel")}</InputLabel>
                                        <Select
                                            error={validate.culture === false}
                                            value={record.culture}
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.handleChange(e)}
                                            inputProps={{ name: 'ddlCulture', id: 'ddlCulture' }}
                                            autoWidth
                                        >
                                            {this.config.cultures.map((item) => (
                                                <MenuItem key={item.key} value={item.key}>
                                                    <ListItemText primary={item.value} disableTypography />
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                }

                                {this.renderPassword()}

                                {
                                    record.userId != null && this.state.currentLockout &&
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    name="isLockedOut"
                                                    checked={record.account.isLockedOut}
                                                    onChange={this.handleChange}
                                                    value="isLockedOut"
                                                    color="secondary"
                                                />
                                            }
                                            label={this.res("LockAccountLabel")}
                                        />
                                    </FormGroup>
                                }


                            </Grid>
                        </Grid>
                    </MuiPaper>
                </form >
            </MuiContainer >
        );
    }

    ///
    private renderFormMenuBar(): JSX.Element {
        return (
            <MuiContainer margin="dense">
                <AppBar position="static" color="secondary">
                    <AppBarTabs centered value={this.state.currentTab} onChange={this.handleTabSelection}>
                        <Tab value={TabOptions.Manager} label={this.res("TabManagerLabel")} style={TabLargeStyle} />
                        <Tab value={TabOptions.Personal} label={this.res("TabPersonalLabel")} style={TabLargeStyle} />
                    </AppBarTabs>
                </AppBar>
            </MuiContainer>
        );
    }

    ///
    private renderFormCorp() {

        const { record } = this.state;
        const x = record.corp.resignationDate;

        return (
            <MuiContainer margin="normal" backdrop={record.deleted}>

                <Card>
                    <CardHeaderColor title={this.res("GroupCorpTitle")} color="primary" />

                    <CardContent>
                        <Grid container spacing={1}>

                            <Grid item xs={12}>
                                {
                                    <ManagerAutoComplete
                                        label={this.res("ManagerLabel")}
                                        emptySelectionLabel={this.res("ManagerPlaceholder")}
                                        margin="dense"
                                        multiple={false}
                                        refUserId={record.userId}
                                        selectedItems={record.corp.manager == null ? null : [record.corp.manager]}
                                        onChange={
                                            (data) =>
                                                this.updateRecord("manager", data == null ? null : data[0])}
                                    />
                                }

                            </Grid>


                            <Grid item xs={6}>
                                <MuiKeyboardDatePicker
                                    fullWidth
                                    clearable
                                    margin="dense"
                                    label={this.res("AdmissionLabel")}
                                    emptyLabel={this.res("AdmissionEmptyLabel")}
                                    value={record.corp == null ? null : record.corp.admissionDate}
                                    onChange={(date) => { this.updateRecord("admissionDate", date?.toDate()); }}
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <MuiKeyboardDatePicker
                                    fullWidth
                                    clearable
                                    margin="dense"
                                    label={this.res("ResignationLabel")}
                                    emptyLabel={this.res("ResignationEmptyLabel")}
                                    value={record.corp == null ? null : record.corp.resignationDate}
                                    onChange={(date) => {this.updateRecord("resignationDate", date?.toDate()); }}
                                />
                            </Grid>

                        </Grid>
                    </CardContent>

                </Card>

            </MuiContainer >
        );
    }

    ///
    private renderFormGroups() {
        return (
            <MuiContainer margin="normal" backdrop={this.state.record.deleted}>

                <UserEditGroup
                    userId={this.state.record.userId}
                    selectedGroups={this.state.record.groups}
                    onChange={(groups) => this.updateRecord("groups", groups)}
                    onEndEdit={() => { }}
                />

            </MuiContainer >
        );
    }

    ///
    private renderFormPermissions() {
        const { ownRoles, groupsRoles, groups } = this.state.record;
        return (
            <MuiContainer margin="normal" backdrop={this.state.record.deleted}>
                <UserEditRole
                    groups={this.state.record.groups.map((v) => v.id)}
                    ownRoles={ownRoles}
                    groupsRoles={groupsRoles}
                    onChange={(v) => this.updateRecord("ownRoles", v)}
                    onSelectedGroupsChange={
                        (newGroups) => {
                            const g: GroupShortView[] = [];
                            groups.forEach((currentGroup) => {
                                const ng = newGroups.find(x => x.id == currentGroup.id);
                                if (ng == null || ng.checked) g.push(currentGroup);
                            });
                            this.updateRecord("groups", g);
                        }
                    }
                />
            </MuiContainer >
        );
    }

    ///
    private renderFormCredentials() {
        return (
            <MuiContainer margin="normal" backdrop={this.state.record.deleted}>

                <UserEditCredentials
                    credentials={this.state.record.personal.credentials}
                    onChange={(credentials) => this.updateRecord("credentials", credentials)}
                />

            </MuiContainer >
        );
    }

    ///
    private renderFormKnowsAbout() {
        return (
            <MuiContainer margin="normal" backdrop={this.state.record.deleted}>

                <UserEditKnowsAbout
                    readonly={false}
                    userId={this.state.record.userId}
                    userName={this.state.record.name}
                    knowsAbout={this.state.record.knowsAbout}
                    onChange={(knowsAbout) => this.updateRecord("knowsAbout", knowsAbout)}
                />

            </MuiContainer >
        );

    }

    ///
    private renderFormPhones() {
        return (
            <MuiContainer margin="normal" backdrop={this.state.record.deleted}>

                <UserEditPhones
                    editResidentialPhones={Session.user.id == this.state.record.userId}
                    phones={this.state.record.personal.phones}
                    onChange={(phones) => this.updateRecord("phones", phones)}
                    message={
                        <MuiMessageBox
                            size="small"
                            hideIcon={true}
                            variant="notice"
                            message={
                                <ul style={{ listStyle: "square" }}>
                                    <li>{this.res("PhonesL1")}</li>
                                    <li>{this.res("PhonesL2")}</li>
                                    <li>{this.res("PhonesL3")}</li>
                                </ul>
                            }
                        />
                    }
                />

            </MuiContainer >
        );
    }

    ///
    private renderFormSocialNetworks() {
        return (
            <MuiContainer margin="normal" backdrop={this.state.record.deleted}>

                <UserEditSocialNetworks
                    networks={this.state.record.personal.socialNetworks}
                    onChange={(networks) => this.updateRecord("networks", networks)}
                />

            </MuiContainer >
        );
    }

    ///
    public componentDidMount() {
        this.init();
    }

    ///
    public componentWillUnmount() {
        window.onbeforeunload = undefined;
    }

    ///
    public componentDidUpdate() {
        if (this.isRecordChanged()) {
            window.onbeforeunload = () => true;
        } else {
            window.onbeforeunload = undefined;
        }
    }

    ///
    public render() {
        if (this.state.formCycle == RequestLifeCycle.Init) {
            return <RequestIndicator type="waiting" />;
        }

        const unsaveMessage = this.props.t("Common.Question.Unsave");

        const { currentTab, dialog, formCycle, lastError, record } = this.state;

        return (
            <>
                {dialog}

                {formCycle == RequestLifeCycle.Posting && <RequestIndicator type="post" />}

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

                {
                    lastError && formCycle != RequestLifeCycle.PostFailed
                        ? <RequestErrorReport
                            error={lastError}
                            action={
                                <ButtonBack
                                    text={this.props.gobackMessage}
                                    onClick={() => this.props.onClose(false)}
                                />
                            }
                        />
                        : (
                            <Grid container justifyContent="space-around">
                                <Grid item xs>
                                    <div className={this.props.classes.formContainer}>

                                        {this.renderToolbar()}

                                        <RenderAccountTopHeaderInfo accountType={record?.account?.accountType} />

                                        {
                                            this.loadDto.userId != null
                                            && !this.loadDto.blocked
                                            && (this.loadDto.blockCodeId != (record.blockCodeId == "" ? null : record.blockCodeId) || this.loadDto.ownRoles != record.ownRoles || this.loadDto.groupsRoles != record.groupsRoles)
                                            &&
                                            <MuiContainer margin="dense">
                                                <Alert severity="info" variant="filled">{this.res("UserWillLogout")}</Alert>
                                            </MuiContainer>
                                        }

                                        {
                                            !this.config.hasLicenceAvailable && record.userId == null &&
                                            <MuiContainer margin="dense">
                                                <Alert severity="warning" variant="filled">
                                                    <small>
                                                        <ReactSplitString
                                                            text={this.res("NoLicenseMessageCreate", { count: this.config.maxUsers })}
                                                            splitChar={"\n"}
                                                            separator={<br />}
                                                        />
                                                    </small>
                                                </Alert>
                                            </MuiContainer>
                                        }

                                        {
                                            !this.config.hasLicenceAvailable && record.userId != null && record.blocked &&
                                            <MuiContainer margin="dense">
                                                <Alert severity="warning" variant="filled">
                                                    <small>
                                                        <ReactSplitString
                                                            text={this.res("NoLicenseMessage")}
                                                            splitChar={"\n"}
                                                            separator={<br />}
                                                        />
                                                    </small>
                                                </Alert>
                                            </MuiContainer>
                                        }

                                        {this.renderFormMain()}

                                        {this.renderFormMenuBar()}

                                        {
                                            currentTab == TabOptions.Manager
                                                ? (
                                                    <>
                                                        {this.config.corporateMode && this.renderFormCorp()}
                                                        {this.renderFormGroups()}
                                                        {this.renderFormPermissions()}
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        {this.renderFormCredentials()}
                                                        {this.renderFormKnowsAbout()}
                                                        {this.renderFormPhones()}
                                                        {this.renderFormSocialNetworks()}
                                                    </>
                                                )
                                        }

                                        {this.renderRecordInfo()}
                                    </div>
                                </Grid>
                            </Grid>
                        )
                }

                <Prompt
                    when={this.isRecordChanged()}
                    message={unsaveMessage}
                />
            </>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(FormStyleClasses)(UserEdit)));
